import React,{ useEffect, useState} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

import BasicSelector from '../../components/ui/BasicSelector.jsx'

import {setCurrentCompany} from '../../store/currentCompanySlice.js'
import {setCurrentBranch} from '../../store/currentBranchSlice.js'
import { setSettings } from '../../store/settingSlice.js'
import { setLang } from '../../store/langSlice.js'
import { appStorageKey } from '../../utils/global.js'

// ICONS
import {MdOutlineHomeWork} from 'react-icons/md'
import {AiOutlineBranches} from 'react-icons/ai'

const TenantBar = ({companiesList,branchesList,companySelected,branchSelected}) => {
    
    const dispatch = useDispatch();
    const { i18n } = useTranslation('global');
    const user = useSelector((state) => state.auth).user;
    const langState = useSelector((state) => state.lang);
    const isRTL = i18n.dir() === 'rtl';
    const [openListCompanies,setOpenListCompanies] = useState(false);
    const [openListBranches,setOpenListBranches] = useState(false);
    const [recentUserSelection, setRecentUserSelection] = useState(null);

    const CENTRAL_SETTINGS_SCOPE = 'global';
    const CENTRAL_SELECTION_SCOPE = 'header-selection';
    const CENTRAL_SETTINGS_CACHE_PREFIX = 'centralSettingsCache:';

    const safeParse = (value) => {
        if (!value) return null;
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    };
    const mergeUserSettings = (settings, name, value) => {
        const list = Array.isArray(settings) ? settings : [];
        const filtered = list.filter((item) => item?.name !== name);
        if (value === undefined) return filtered;
        return [...filtered, { name, value }];
    };
    const updateStoredUser = (payload) => {
        const existing = safeParse(localStorage.getItem('user')) || {};
        const next = { ...existing, ...payload };
        localStorage.setItem('user', JSON.stringify(next));
    };
    const updateCurrentUser = async (payload) => {
        if (!payload) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_Auth_API_BASE_URL}/users/update`,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    validateStatus: () => true
                }
            );
            if (response?.status === 200 && response?.data?.data) {
                updateStoredUser(response.data.data);
            }
        } catch (error) {
        }
    };

    const getNameByLang = (nameList) => {
        if (!Array.isArray(nameList)) return '';
        return (
            nameList.find((n) => n?.lang === i18n.language)?.text ||
            nameList.find((n) => n?.lang === 'ar')?.text ||
            nameList.find((n) => n?.lang === 'en')?.text ||
            nameList?.[0]?.text ||
            ''
        );
    };
    const getLangLabel = (langCode) => {
        if (!langCode) return '';
        return langCode === 'ar' ? 'عربي' : langCode === 'en' ? 'English' : langCode;
    };

    const loadCentralSettings = async (companyId) => {
        if (!user?.tenant_id || !companyId) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        const cacheKey = `${CENTRAL_SETTINGS_CACHE_PREFIX}${user.tenant_id}:${companyId}:${CENTRAL_SETTINGS_SCOPE}`;
        const cachedRaw = safeParse(localStorage.getItem(cacheKey));
        if (cachedRaw?.data) {
            dispatch(setSettings({
                invoiceSettings: cachedRaw?.data?.invoiceSettings || {},
                generalSettings: cachedRaw?.data?.generalSettings || {},
                quoteSettings: cachedRaw?.data?.quoteSettings || {}
            }));
        }
        const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&companyId=${companyId}&scope=${CENTRAL_SETTINGS_SCOPE}`;
        try {
            const headers = {
                Authorization: `Bearer ${token}`
            };
            if (cachedRaw?.version) {
                headers['If-None-Match'] = String(cachedRaw.version);
            }
            const response = await axios.get(url, { headers, validateStatus: () => true });
            if (response.status === 304) return;
            if (response?.data?.status === 1) {
                const responseData = response?.data?.data;
                if (!responseData && cachedRaw?.data) return;
                const nextData = responseData || {};
                const nextVersion = response?.data?.version ?? cachedRaw?.version ?? 0;
                localStorage.setItem(cacheKey, JSON.stringify({ data: nextData, version: nextVersion }));
                dispatch(setSettings({
                    invoiceSettings: nextData?.invoiceSettings || {},
                    generalSettings: nextData?.generalSettings || {},
                    quoteSettings: nextData?.quoteSettings || {}
                }));
            }
        } catch (error) {
        }
    };
    const getTenantData = () => safeParse(localStorage.getItem('tenant'));
    const getCompanyById = (tenantData, companyId) =>
        tenantData?.companies?.find((company) => company?._id === companyId);
    const getBranchById = (tenantData, companyId, branchId) => {
        const company = getCompanyById(tenantData, companyId);
        return company?.branches?.find((branch) => branch?._id === branchId);
    };
    const loadCentralSelection = async () => {
        if (!user?.tenant_id) return;
        
        // Check for recent user selections (within last 2 seconds)
        if (recentUserSelection && (Date.now() - recentUserSelection.timestamp < 2000)) {
            return; // Skip loading central selection if user just made a selection
        }
        
        const token = localStorage.getItem('token');
        if (!token) return;
        const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&scope=${CENTRAL_SELECTION_SCOPE}`;
        try {
            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true });
            if (response?.data?.status !== 1) return;
            const selection = response?.data?.data;
            if (!selection) return;
            const responseCompany = response?.data?.currentCompany;
            const responseBranch = response?.data?.currentBranch;
            const tenantData = getTenantData();
            const selectedCompanyId = selection?.selectedCompanyId || selection?.companyId;
            const selectedBranchId = selection?.selectedBranchId || selection?.branchId;
            const selectedLang = selection?.selectedLang || selection?.lang || selection?.language;
            if (selectedCompanyId) {
                const company = getCompanyById(tenantData, selectedCompanyId);
                const companyName = responseCompany?.name ? getNameByLang(responseCompany.name) : getNameByLang(company?.name);
                if (company || companyName) {
                    dispatch(setCurrentCompany({
                        name: companyName,
                        value: selectedCompanyId
                    }));
                }
            }
            const effectiveCompanyId = selectedCompanyId || companySelected?.value;
            if (effectiveCompanyId && selectedBranchId) {
                const branch = getBranchById(tenantData, effectiveCompanyId, selectedBranchId);
                const branchName = responseBranch?.name ? getNameByLang(responseBranch.name) : getNameByLang(branch?.name);
                if (branch || branchName) {
                    dispatch(setCurrentBranch({
                        name: branchName || (isRTL ? 'الفرع' : 'Branch'),
                        value: selectedBranchId
                    }));
                }
            }
            if (selectedLang) {
                dispatch(setLang({ label: getLangLabel(selectedLang), value: selectedLang }));
                i18n.changeLanguage(selectedLang);
            }
        } catch (error) {
        }
    };
    const saveCentralSelection = async (selectedCompanyId, selectedBranchId, selectedLang) => {
        if (!user?.tenant_id) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        try {
            await axios.put(
                `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central`,
                {
                    tenantId: user.tenant_id,
                    scope: CENTRAL_SELECTION_SCOPE,
                    data: {
                        selectedCompanyId,
                        selectedBranchId,
                        selectedLang: selectedLang || langState?.value || i18n.language
                    }
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    validateStatus: () => true
                }
            );
        } catch (error) {
        }
    };

    const onSelectedCompany = async (company) => {
        dispatch(setCurrentCompany({name:company.label,value:company.value}));
        setRecentUserSelection({ type: 'company', value: company.value, timestamp: Date.now() });

        const tenantData = safeParse(localStorage.getItem('tenant'));
        const nextCompany = tenantData?.companies?.find((c) => c?._id === company?.value);
        const nextBranch = nextCompany?.branches?.[0];
        if (nextBranch?._id) {
            dispatch(setCurrentBranch({name: getNameByLang(nextBranch?.name) || (isRTL ? 'الفرع' : 'Branch'), value: nextBranch?._id}));
        } else {
            localStorage.removeItem(appStorageKey('selectedBranch'));
        }
        const nextUserSettings = mergeUserSettings(user?.userSettings, 'selectedCompany', company.value);
        await updateCurrentUser({
            company_id: company.value,
            branch_id: nextBranch?._id,
            userSettings: nextUserSettings
        });
        await saveCentralSelection(company.value, nextBranch?._id, langState?.value || i18n.language);
        loadCentralSettings(company.value);
    }

    const onSelectedBranch = async (branch) => {
        dispatch(setCurrentBranch({name:branch.label,value:branch.value,data:branch.data}));
        setRecentUserSelection({ type: 'branch', value: branch.value, timestamp: Date.now() });
        const nextUserSettings = mergeUserSettings(user?.userSettings, 'selectedBranch', branch.value);
        await updateCurrentUser({
            branch_id: branch.value,
            company_id: companySelected?.value,
            userSettings: nextUserSettings
        });
        await saveCentralSelection(companySelected?.value, branch.value, langState?.value || i18n.language);
        loadCentralSettings(companySelected?.value);
    }
    useEffect(() => {
        loadCentralSelection();
    }, [user?.tenant_id, user?.company_id, companySelected?.value, recentUserSelection]);
    return (
        <div className="flex   flex-wrap md:flex-nowrap justify-center items-center text-xs ms-4 gap-0 w-max">
          <BasicSelector
            border={false}
            label={companySelected.name}
            defaultValue={companySelected.name}
            listItem={companiesList}
            openList={openListCompanies}
            setOpenList={setOpenListCompanies}
            onSelected={onSelectedCompany}
          >
            <img className="w-5" src="/assets/icons/company-svgrepo-com 1.png" alt="Company" />
          </BasicSelector>
    
          {/* <div className="text-slate-200 hidden md:flex">/</div> */}
    
          <BasicSelector
            border={false}
            label={branchSelected.name}
            defaultValue={branchSelected.name}
            listItem={branchesList}
            openList={openListBranches}
            setOpenList={setOpenListBranches}
            onSelected={onSelectedBranch}
          >
            <img className="w-5" src="/assets/icons/location.png" alt="Branch" />
          </BasicSelector>
        </div>
      );
}

export default TenantBar
